/**
 * Email Preferences Page
 * Manage notification preferences and opt-in/opt-out settings
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';

interface Preferences {
  emailEnabled: boolean;
  marketingEmails: boolean;
  complianceAlerts: boolean;
  projectUpdates: boolean;
  subscriptionUpdates: boolean;
  weeklyDigest: boolean;
}

const EmailPreferences: React.FC = () => {
  const navigate = useNavigate();
  
  const [preferences, setPreferences] = useState<Preferences>({
    emailEnabled: true,
    marketingEmails: false,
    complianceAlerts: true,
    projectUpdates: true,
    subscriptionUpdates: true,
    weeklyDigest: false,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/email/preferences', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch preferences');
      }

      const data = await response.json();
      setPreferences(data.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch('/api/v1/email/preferences', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(preferences),
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key: keyof Preferences) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading preferences..." />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Email Preferences</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage your notification preferences and control what emails you receive
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">✓ Preferences saved successfully</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Preferences Form */}
      <div className="bg-white rounded-lg shadow-sm">
        {/* Master Toggle */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Email Notifications</h3>
              <p className="mt-1 text-sm text-gray-500">
                Enable or disable all email notifications
              </p>
            </div>
            <button
              onClick={() => handleToggle('emailEnabled')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                preferences.emailEnabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  preferences.emailEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Category Toggles */}
        <div className="p-6 space-y-6">
          {/* Compliance Alerts */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center">
                <span className="text-2xl mr-3">⚠️</span>
                <div>
                  <h4 className="text-base font-medium text-gray-900">Compliance Alerts</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Critical NFPA 72 code violations and compliance issues
                  </p>
                  <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                    Recommended
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => handleToggle('complianceAlerts')}
              disabled={!preferences.emailEnabled}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                preferences.complianceAlerts && preferences.emailEnabled
                  ? 'bg-blue-600'
                  : 'bg-gray-200'
              } ${!preferences.emailEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  preferences.complianceAlerts && preferences.emailEnabled
                    ? 'translate-x-5'
                    : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Subscription Updates */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center">
                <span className="text-2xl mr-3">💳</span>
                <div>
                  <h4 className="text-base font-medium text-gray-900">Subscription Updates</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Billing, payment confirmations, and subscription changes
                  </p>
                  <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    Transactional
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => handleToggle('subscriptionUpdates')}
              disabled={!preferences.emailEnabled}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                preferences.subscriptionUpdates && preferences.emailEnabled
                  ? 'bg-blue-600'
                  : 'bg-gray-200'
              } ${!preferences.emailEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  preferences.subscriptionUpdates && preferences.emailEnabled
                    ? 'translate-x-5'
                    : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Project Updates */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center">
                <span className="text-2xl mr-3">📁</span>
                <div>
                  <h4 className="text-base font-medium text-gray-900">Project Updates</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Project creation, sharing, and calculation completions
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => handleToggle('projectUpdates')}
              disabled={!preferences.emailEnabled}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                preferences.projectUpdates && preferences.emailEnabled
                  ? 'bg-blue-600'
                  : 'bg-gray-200'
              } ${!preferences.emailEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  preferences.projectUpdates && preferences.emailEnabled
                    ? 'translate-x-5'
                    : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Weekly Digest */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center">
                <span className="text-2xl mr-3">📊</span>
                <div>
                  <h4 className="text-base font-medium text-gray-900">Weekly Digest</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Weekly summary of your activity and project statistics
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => handleToggle('weeklyDigest')}
              disabled={!preferences.emailEnabled}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                preferences.weeklyDigest && preferences.emailEnabled
                  ? 'bg-blue-600'
                  : 'bg-gray-200'
              } ${!preferences.emailEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  preferences.weeklyDigest && preferences.emailEnabled
                    ? 'translate-x-5'
                    : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Marketing Emails */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center">
                <span className="text-2xl mr-3">📧</span>
                <div>
                  <h4 className="text-base font-medium text-gray-900">Marketing Emails</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Product updates, tips, and promotional offers
                  </p>
                  <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    Optional
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => handleToggle('marketingEmails')}
              disabled={!preferences.emailEnabled}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                preferences.marketingEmails && preferences.emailEnabled
                  ? 'bg-blue-600'
                  : 'bg-gray-200'
              } ${!preferences.emailEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  preferences.marketingEmails && preferences.emailEnabled
                    ? 'translate-x-5'
                    : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Changes are saved immediately
          </p>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              'Save Preferences'
            )}
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">About Email Preferences</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>Transactional emails</strong> (subscription, security) cannot be disabled
                </li>
                <li>
                  <strong>Compliance alerts</strong> are highly recommended for code violations
                </li>
                <li>You can opt out of marketing emails anytime</li>
                <li>Changes take effect immediately</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPreferences;
